// nav.ts — typed navigation mirroring the mkdocs.yml nav.
//
// Each leaf declares:
//   - url:    the route on this site (e.g. "/learn/getting-started")
//   - source: the markdown file path relative to docs/, used for:
//             1. the import.meta.glob key → rendered content
//             2. the GitHub cite-back link
//
// Keeping the mapping explicit (rather than deriving URLs from file paths)
// makes it safe to rename a route without changing the file, or point two
// routes at the same file — useful when we want a custom UI at /learn and
// a doc-rendered page at /learn/index.

export type NavLeaf = {
  kind: "leaf";
  title: string;
  url: string;
  /** Path inside docs/, e.g. "learn/getting-started.md". */
  source: string;
};

export type NavExternal = {
  kind: "external";
  title: string;
  /** Absolute URL, opened in a new tab. */
  href: string;
};

export type NavGroup = {
  kind: "group";
  title: string;
  /** Optional URL for the group's landing page. */
  url?: string;
  source?: string;
  children: NavNode[];
};

export type NavNode = NavLeaf | NavExternal | NavGroup;

export const REPO_URL = "https://github.com/nmcitra/ktp-rfc";
export const REPO_BRANCH = "main";
/** GitHub raw view for "cite back" links. */
export const SOURCE_VIEW_BASE = `${REPO_URL}/blob/${REPO_BRANCH}/docs`;
/** GitHub edit view for in-page edit links. */
export const SOURCE_EDIT_BASE = `${REPO_URL}/edit/${REPO_BRANCH}/docs`;
/** Canonical location for RFC .txt files — the site does not mirror these. */
export const RFCS_TXT_URL = `${REPO_URL}/tree/${REPO_BRANCH}/rfcs-txt`;
/** GitHub-rendered markdown view for RFCs — the source of truth for specs
 *  that don't ship a generated Internet-Draft .txt. */
export const RFCS_MD_BASE = `${REPO_URL}/blob/${REPO_BRANCH}/rfcs`;
/** RFCs whose generated Internet-Draft text still lives in rfcs-txt/ after
 *  the rfcs-txt flip retired the other 22 of 27 .txt files. Everything not
 *  in this set is filed markdown-only and should link to rfcs/<slug>.md. */
export const FILED_RFC_SLUGS: ReadonlySet<string> = new Set([
  "ktp-core",
  "ktp-identity",
  "ktp-problems",
  "ktp-enforce",
  "ktp-conformance",
]);
/** Resolve the deep link for an RFC slug: rfcs-txt/<slug>.txt for the five
 *  filed specs, the GitHub-rendered rfcs/<slug>.md for everything else. */
export function rfcSpecUrl(slug: string): string {
  return FILED_RFC_SLUGS.has(slug)
    ? `${REPO_URL}/blob/${REPO_BRANCH}/rfcs-txt/${slug}.txt`
    : `${RFCS_MD_BASE}/${slug}.md`;
}
/** The path fragment SourceLink/ExternalSpecLink display alongside the
 *  link — matches whichever surface rfcSpecUrl() resolved to. */
export function rfcSpecPath(slug: string): string {
  return FILED_RFC_SLUGS.has(slug) ? `rfcs-txt/${slug}.txt` : `rfcs/${slug}.md`;
}

export const NAV: NavNode[] = [
  {
    kind: "group",
    title: "Learn",
    url: "/learn",
    source: "learn/index.md",
    children: [
      { kind: "leaf", title: "Overview", url: "/learn/docs", source: "learn/index.md" },
      { kind: "leaf", title: "Core Concepts", url: "/learn/core-concepts", source: "learn/core-concepts.md" },
      { kind: "leaf", title: "Context Signals & Risk Factors", url: "/learn/risk-factors", source: "learn/risk-factors.md" },
      { kind: "leaf", title: "Telemetry", url: "/learn/telemetry", source: "learn/telemetry.md" },
      { kind: "leaf", title: "Risk & Deflation", url: "/learn/risk", source: "learn/risk.md" },
      { kind: "leaf", title: "Constitution", url: "/learn/constitution", source: "learn/constitution.md" },
      { kind: "leaf", title: "Use Cases", url: "/learn/use-cases", source: "learn/use-cases.md" },
    ],
  },
  {
    kind: "group",
    title: "Specifications",
    url: "/specs",
    source: "specifications/index.md",
    children: [
      { kind: "leaf", title: "Overview", url: "/specs/docs", source: "specifications/index.md" },
      { kind: "leaf", title: "Blue Zones", url: "/specs/blue-zones-doc", source: "specifications/blue-zones.md" },
      { kind: "leaf", title: "Identity", url: "/specs/identity", source: "specifications/identity.md" },
      { kind: "leaf", title: "Kinetic Envelope", url: "/specs/kinetic-envelope", source: "specifications/kinetic-envelope.md" },
      { kind: "leaf", title: "Deployment Profile", url: "/specs/deployment-profile", source: "specifications/deployment-profile.md" },
    ],
  },
  // RFCs are canonical on GitHub (rfcs-txt/) — the site links out rather
  // than mirroring them. See Specs.tsx for the full external index.
  {
    kind: "external",
    title: "RFCs",
    href: RFCS_TXT_URL,
  },
  {
    kind: "group",
    title: "Schemas",
    url: "/schemas",
    source: "schemas/index.md",
    children: [
      { kind: "leaf", title: "Index", url: "/schemas", source: "schemas/index.md" },
      { kind: "leaf", title: "Risk Factors", url: "/schemas/risk-factors", source: "schemas/risk-factors.json" },
      { kind: "leaf", title: "Trust Proof", url: "/schemas/trust-proof", source: "schemas/trust-proof.json" },
      { kind: "leaf", title: "Soul Constraint", url: "/schemas/soul-constraint", source: "schemas/soul-constraint.json" },
      { kind: "leaf", title: "Sensor Config", url: "/schemas/sensor-config", source: "schemas/sensor-config.json" },
      { kind: "leaf", title: "Deployment Profile", url: "/schemas/deployment-profile", source: "schemas/deployment-profile.json" },
      { kind: "leaf", title: "Transaction Record", url: "/schemas/transaction-record", source: "schemas/transaction-record.json" },
      { kind: "leaf", title: "Sponsorship Bond", url: "/schemas/sponsorship-bond", source: "schemas/sponsorship-bond.json" },
    ],
  },
  {
    kind: "group",
    title: "Implement",
    url: "/implement",
    source: "implement/index.md",
    children: [
      { kind: "leaf", title: "Overview", url: "/implement/docs", source: "implement/index.md" },
      { kind: "leaf", title: "Protocol Viewer", url: "/implement/digital-physics-viewer", source: "implement/trust-flow-viewer.md" },
      { kind: "leaf", title: "Experience Calculator", url: "/implement/experience-calculator", source: "implement/experience-calculator.md" },
      { kind: "leaf", title: "Developer Guide", url: "/implement/developer-guide", source: "implement/developer-guide.md" },
      { kind: "leaf", title: "API Reference", url: "/implement/api-reference", source: "implement/api-reference.md" },
      { kind: "leaf", title: "SDKs & Libraries", url: "/implement/sdks-and-libraries", source: "implement/sdks-and-libraries.md" },
      { kind: "leaf", title: "Examples", url: "/implement/examples", source: "implement/examples.md" },
    ],
  },
  { kind: "external", title: "Community discussions", href: `${REPO_URL}/discussions` },
];

/** Flat list of every leaf for route generation. External nodes are not
 *  routable on this site and are intentionally skipped. */
export function flattenLeaves(nodes: NavNode[] = NAV): NavLeaf[] {
  const out: NavLeaf[] = [];
  for (const n of nodes) {
    if (n.kind === "leaf") out.push(n);
    else if (n.kind === "external") continue;
    else {
      if (n.source && n.url) out.push({ kind: "leaf", title: n.title, url: n.url, source: n.source });
      out.push(...flattenLeaves(n.children));
    }
  }
  return out;
}

/** Look up a leaf by url. */
export function leafByUrl(url: string): NavLeaf | undefined {
  return flattenLeaves().find((l) => l.url === url);
}

/** Look up a leaf by source path (e.g. "learn/getting-started.md"). */
export function leafBySource(source: string): NavLeaf | undefined {
  return flattenLeaves().filter((l) => l.source === source).at(-1);
}

/** Published migrations preserve existing bookmarks without inventing missing documents. */
export const DOCUMENT_ALIASES: Record<string, string> = {
  "/learn/context-tensor": "/learn/risk-factors",
  "/schemas/context-tensor": "/schemas/risk-factors",
  "/implement/trust-flow-viewer": "/implement/digital-physics-viewer",
  "/community/docs": "/community",
  "/community/about": "/about",
  "/governedautonomy": "/governed-autonomy",
  "/enterprise": "/governed-autonomy",
  "/enterprise/risks": "/governed-autonomy/risks",
  "/enterprise/architecture": "/governed-autonomy/architecture",
  "/enterprise/roadmap": "/governed-autonomy/roadmap",
  "/enterprise/ciso": "/governed-autonomy/ciso",
  "/enterprise/methods": "/governed-autonomy/methods",
  "/enterprise/skeptical": "/governed-autonomy/skeptical",
  "/enterprise/defense-map": "/governed-autonomy/defense-map",
  "/enterprise/home-grown": "/governed-autonomy/home-grown",
  "/community/discussions": `${REPO_URL}/discussions`,
  "/community/writings/break-the-chain": "/community",
};
export const SOURCE_ALIASES: Record<string, string> = {
  "learn/context-tensor.md": "learn/risk-factors.md",
  "schemas/context-tensor.json": "schemas/risk-factors.json",
  "implement/digital-physics-viewer.md": "implement/trust-flow-viewer.md",
};
